import { estEffetDeFiche, identiteDuJeu } from '@taki/data-model';
import type { AppScreen, ContexteDeCondition } from '@taki/data-model';
import type { FamilleRepository, TakiSdk } from '@taki/sdk';
import type {
  AccesJeu,
  ActionAssemblee,
  ContexteDAction,
  EffetDAction,
  ChampDeclare,
  FavoriAssemble,
  FiltreAssemble,
  FonctionsDeclarees,
  GestesDeFiche,
  PorteAssemblee,
  StatAssemblee,
  TriAssemble,
  ViewBinding,
  ViewId,
  ViewType,
} from '@taki/ui';
import {
  actionsDe,
  bindingsDe,
  champsDuJeu,
  favorisDe,
  filtresDe,
  portesDe,
  statsDe,
  trancheDe,
  triDe,
  verbesDe,
  vuesDe,
} from '@taki/ui';
import { calendrier, fiche, kanban, liste, tableau } from '@taki/ui/views/core';
import type { FicheGenerique } from './app-declaree.js';
import { accesDuJeu } from './acces.js';
import { qualifierLesNatifs } from './natifs.js';
import { totalLieDepuisJeu } from './totaux-lies.js';
import { effetDuSocle, estEffetSocle } from './effets-socle.js';
import type { AvisDEffet, EffetDAppDeclaree } from './effets-socle.js';
import type { DeclarationEffective } from './resolution.js';

/**
 * L'ASSEMBLAGE D'UN ECRAN DECLARE : ce que `views.ts` faisait dans chaque app,
 * fait une fois pour toutes.
 *
 * CE QU'IL FAIT. Il prend une declaration deja validee et un SDK, et il rend ce
 * que le moteur d'ecran attend : les portes, le catalogue de vues, le catalogue
 * de tri, et par porte son binding, ses filtres, ses favoris, ses actions, ses
 * stats, ses gestes de fiche et l'acces a son jeu. Chaque ligne appelle un
 * assembleur de `@taki/ui` : rien n'est reinvente ici, tout est CABLE.
 *
 * CE QU'IL EST AUSSI, ET C'EST PRESQUE PLUS IMPORTANT : la SEULE description de
 * cet assemblage. L'ecran et le panneau de reglages le partagent, donc la ligne
 * « Vue par defaut » grise les memes entrees que le selecteur de vues, et le hub
 * montre exactement le catalogue de filtres que l'outil filtre propose. Deux
 * assemblages pour un ecran finiraient par ne plus se ressembler.
 *
 * FONCTION, ET PAS MODULE DE CONSTANTES : l'acces au jeu a besoin du SDK, et la
 * declaration effective peut venir de l'ORG (`resoudreDeclaration`). L'appelant
 * le memoise sur ces deux-la.
 */

/** Le jeu d'une porte : la famille du SDK et le sous-type de l'app. */
export interface JeuDePorte {
  family: string;
  type?: string;
}

/** Ce que l'ecran et les reglages lisent d'un ecran declare. */
export interface EcranAssemble {
  /** L'ecran valide, pour ce que les assembleurs ne rendent pas (les mots d'une porte). */
  ecran: AppScreen;
  portes: PorteAssemblee[];
  porteArrivee: string;
  vueArrivee: ViewId | undefined;
  types: ViewType[];
  tri: TriAssemble;
  champsDePorte(porteId: string): readonly ChampDeclare[];
  /** Le jeu que cette porte MONTRE, ou `null` quand elle n'en declare aucun. */
  jeuDePorte(porteId: string): JeuDePorte | null;
  /** Le repository de ce jeu, ou `null` : une porte sans jeu reste en lecture. */
  depotDePorte(porteId: string): FamilleRepository | null;
  bindings(porteId: string): ViewBinding<FicheGenerique>[];
  /** `contexte` : ce que les conditions lisent au rendu (la personne connectee). */
  filtres(porteId: string, contexte?: ContexteDeCondition): FiltreAssemble<FicheGenerique>[];
  favoris(porteId: string): FavoriAssemble[];
  /** Le predicat de la tranche de la porte, ou `null` : elle montre tout son jeu. */
  tranche(porteId: string, contexte?: ContexteDeCondition): ((fiche: FicheGenerique) => boolean) | null;
  actions(porteId: string): ActionAssemblee[];
  stats(porteId: string): (live: readonly FicheGenerique[]) => StatAssemblee[];
  gestes(porteId: string): GestesDeFiche<FicheGenerique>;
  /** Les jeux DISTINCTS de l'ecran, avec leur depot : une lecture par jeu, jamais par porte. */
  jeux: readonly { clef: string; jeu: JeuDePorte; depot: FamilleRepository }[];
  /** La clef du jeu d'une porte (`family::type`), ou `null`. */
  clefDeJeuDePorte(porteId: string): string | null;
}

/** La clef d'un jeu dans les tables de ce module : `family::type`. */
export const clefDeJeu = (jeu: JeuDePorte): string => `${jeu.family}::${jeu.type ?? ''}`;

/**
 * LES CINQ VUES DU SOCLE, IMPORTEES EXPLICITEMENT : sans cet import, le build
 * les supprime. La declaration dit LESQUELLES sont montees et dans quel ordre.
 */
const VUES_DU_SOCLE: ViewType[] = [liste, tableau, kanban, calendrier, fiche];

/**
 * CE QUE L'ECRAN PRETE AUX EFFETS D'ACTION, lu AU CLIC et jamais a
 * l'assemblage : l'assemblage est memoise pour la vie de l'ecran, la fiche
 * ouverte et la selection changent a chaque geste. L'ecran passe donc des
 * LECTURES (stables), pas des valeurs.
 *
 * ABSENT (les reglages, un test) : aucune fiche, aucune selection, aucune org,
 * et un effet du socle ne recharge ni n'annonce rien.
 */
export interface EnvironnementDesEffets {
  lireContexte(porteId: string): ContexteDAction;
  orgId: string;
  recharger(): void;
  /** L'avis d'un effet du socle, adresse par la porte et le nom de l'effet. */
  annoncer(porteId: string, effet: string, avis: AvisDEffet): void;
  /**
   * LE JEU CHARGE EN ENTIER, par sa clef (`clefDeJeu`), ou `undefined` tant
   * qu'il ne l'est pas : c'est ce que les TOTAUX LIES par ligne groupent
   * (`totaux-lies.ts`). Absent, ces totaux restent vides.
   */
  lireJeu?(clef: string): readonly FicheGenerique[] | undefined;
}

/**
 * LES EFFETS BRANCHES : ceux de l'app, qui recoivent le SDK et l'org en plus du
 * contexte, et ceux du SOCLE que la declaration nomme (`socle:<nom>`), resolus
 * ici. Un nom `socle:` inconnu LEVE (`effetDuSocle`) : le montage l'a deja
 * refuse, et un bouton muet serait un bouton qui ment.
 */
function effetsBranches(
  ecran: AppScreen,
  sdk: TakiSdk,
  effets: Readonly<Record<string, EffetDAppDeclaree>>,
  environnement: EnvironnementDesEffets | undefined,
): Record<string, EffetDAction> {
  const orgId = environnement?.orgId ?? '';
  const branches: Record<string, EffetDAction> = {};
  for (const [nom, effet] of Object.entries(effets)) {
    branches[nom] = (contexte) => effet({ ...contexte, sdk, orgId });
  }
  for (const porte of ecran.portes) {
    for (const action of porte.actions ?? []) {
      const nom = action.fn;
      /* Un effet de FICHE n'a pas de bouton dans la rangee 3a : le menu « ... »
         de la fiche le tient (`gestes-de-fiche.ts`). */
      if (nom === undefined || !estEffetSocle(nom) || estEffetDeFiche(nom) || branches[nom] !== undefined) continue;
      const effet = effetDuSocle(nom);
      const jeu = porte.jeu;
      branches[nom] = (contexte) => {
        void effet({
          ...contexte,
          sdk,
          orgId,
          ...(jeu ? { jeu: { family: jeu.family, ...(jeu.type ? { type: jeu.type } : {}) } } : {}),
          recharger: () => environnement?.recharger(),
          annoncer: (avis) => environnement?.annoncer(contexte.porteId, nom, avis),
        });
      };
    }
  }
  return branches;
}

export function assemblerEcran(
  declaration: DeclarationEffective,
  sdk: TakiSdk,
  fonctions: FonctionsDeclarees<FicheGenerique>,
  effets: Readonly<Record<string, EffetDAppDeclaree>>,
  environnement?: EnvironnementDesEffets,
): EcranAssemble {
  /* LE SOCLE TYPE SES NATIFS AVANT TOUT LE RESTE (`natifs.ts`) : une instance
     d'org ecrite avant qu'il sache que `workerId` est une personne dit encore
     `text`, et c'est cet ecran-ci, qualifie, que tout l'assemblage lit. */
  const ecran = qualifierLesNatifs(declaration.ecran);
  const effetsDeLEcran = effetsBranches(ecran, sdk, effets, environnement);
  const lireContexte = (porteId: string): ContexteDAction =>
    environnement?.lireContexte(porteId) ?? { porteId, ficheId: null, selection: [] };

  /*
   * LES CHAMPS D'UNE PORTE SONT CEUX DE SON JEU, et une porte qui n'en declare
   * aucun prend ceux du jeu d'ARRIVEE : n'ayant pas de detail par vue a elle,
   * elle herite de celui de l'ecran, qui decrit le jeu d'arrivee. Lui donner une
   * liste VIDE ferait jeter l'assemblage sur la premiere colonne citee.
   */
  const jeuArrivee =
    ecran.portes.find((porte) => porte.id === ecran.porteArrivee)?.jeu ?? ecran.portes[0]?.jeu;
  const champsParPorte = new Map<string, readonly ChampDeclare[]>(
    ecran.portes.map((porte) => {
      const jeu = porte.jeu ?? jeuArrivee;
      return [porte.id, champsDuJeu(declaration.champs, jeu?.family ?? '', jeu?.type)];
    }),
  );
  const champsDePorte = (porteId: string): readonly ChampDeclare[] =>
    champsParPorte.get(porteId) ?? [];

  /*
   * LE JEU QU'UNE PORTE CHARGE EST LE SIEN, ET SEULEMENT LE SIEN.
   *
   * PAS DE REPLI SUR LE JEU D'ARRIVEE ICI, contrairement aux champs, et c'est la
   * difference qui compte : une porte qui ne declare aucun jeu (le suivi d'un
   * chantier qui n'existe pas encore) doit rester VIDE et en lecture. Lui preter
   * le jeu de la porte d'a cote afficherait les fiches d'un autre jeu sous son
   * nom, ce qui est un mensonge, pas un repli.
   */
  const jeuDePorte = (porteId: string): JeuDePorte | null => {
    const jeu = ecran.portes.find((porte) => porte.id === porteId)?.jeu;
    return jeu === undefined ? null : { family: jeu.family, ...(jeu.type === undefined ? {} : { type: jeu.type }) };
  };
  const clefDeJeuDePorte = (porteId: string): string | null => {
    const jeu = jeuDePorte(porteId);
    return jeu === null ? null : clefDeJeu(jeu);
  };

  /* LES JEUX DISTINCTS, dans l'ordre des portes : le chargeur en tire UNE
     requete par jeu, jamais une par porte (trois portes du meme jeu ne font pas
     trois lectures), et les compteurs du menu portent quand meme sur le jeu
     COMPLET de chacune (regle G15). */
  const jeux: { clef: string; jeu: JeuDePorte }[] = [];
  for (const porte of ecran.portes) {
    const jeu = jeuDePorte(porte.id);
    if (jeu === null) continue;
    const clef = clefDeJeu(jeu);
    if (!jeux.some((connu) => connu.clef === clef)) jeux.push({ clef, jeu });
  }

  /* UN REPOSITORY PAR JEU, resolu une fois : `sdk.famille` LEVE sur une famille
     inconnue, et c'est voulu (un nom faux doit se voir au montage, pas a la
     premiere ecriture). */
  const avecDepot = jeux.map(({ clef, jeu }) => ({ clef, jeu, depot: sdk.famille(jeu.family) }));
  const depots = new Map<string, FamilleRepository>(
    avecDepot.map(({ clef, depot }) => [clef, depot]),
  );
  const depotDePorte = (porteId: string): FamilleRepository | null => {
    const clef = clefDeJeuDePorte(porteId);
    return clef === null ? null : (depots.get(clef) ?? null);
  };

  /* L'ACCES AU JEU D'UNE PORTE : le catalogue de colonnes de l'ECRAN voyage avec
     lui, parce que c'est lui qui porte les champs natifs qui refusent de se
     vider (`requis`). */
  const acces = (porteId: string): AccesJeu<FicheGenerique> =>
    accesDuJeu(depotDePorte(porteId), ecran.colonnes ?? [], undefined, champsDePorte(porteId));

  const portes = portesDe(ecran, champsDePorte);

  const types = vuesDe(ecran.vues, VUES_DU_SOCLE);
  const vueArrivee = types.find((vue) => vue.id === ecran.arrivee)?.id;
  const porteArrivee = ecran.porteArrivee ?? ecran.portes[0]?.id ?? '';

  /*
   * LE TRI EST UN FAIT DE L'ECRAN, pas d'un jeu : un ecran dont les portes
   * montrent deux jeux propose des tris qui viennent des DEUX. Lui passer les
   * champs du seul jeu d'arrivee ferait jeter au chargement, avec « le tri sur X
   * n'a de libelle ni dans le catalogue de colonnes, ni dans taki.fields.json ».
   */
  const tousLesChamps = (declaration.champs.jeux ?? []).flatMap((jeu) => jeu.champs ?? []);
  const tri = triDe(ecran, tousLesChamps);

  /* LES TOTAUX LIES PAR LIGNE se calculent sur le jeu lie que l'ecran a deja
     charge EN ENTIER (`totaux-lies.ts`) ; une app qui fournit le sien garde la
     main. */
  const fonctionsDeLEcran: FonctionsDeclarees<FicheGenerique> = {
    ...fonctions,
    totalLie:
      fonctions.totalLie ??
      ((total) => {
        const lireJeu = environnement?.lireJeu;
        if (lireJeu === undefined) return undefined;
        const clef = clefDeJeu({
          family: total.link.family,
          ...(total.link.type === undefined ? {} : { type: total.link.type }),
        });
        return totalLieDepuisJeu(total, () => lireJeu(clef));
      }),
  };

  /* UN ASSEMBLAGE PAR PORTE, fait UNE fois : les recreer a chaque rendu
     relancerait la projection a chaque frappe dans la recherche. */
  const bindingsParPorte = new Map<string, ViewBinding<FicheGenerique>[]>(
    ecran.portes.map((porte) => [
      porte.id,
      bindingsDe(
        ecran,
        champsDePorte(porte.id),
        acces(porte.id),
        fonctionsDeLEcran,
        porte.id,
        champsDePorte,
        { timezone: sdk.timezone },
        /* LA COULEUR ET LA PHOTO que le jeu de la porte allume (`jeux[].couleur`,
           `jeux[].photo`) : deux roles de plus sur chaque binding de la porte. */
        identiteDuJeu(declaration.champs, jeuDePorte(porte.id)),
      ),
    ]),
  );

  /* LA LECTURE SEULE SUFFIT AUX FILTRES ET AUX STATS : ils lisent, ils n'ecrivent
     jamais. C'est ce qui permet au panneau de reglages de montrer le MEME
     catalogue que l'ecran, plutot qu'une seconde liste a tenir d'accord. */
  const lecture = accesDuJeu(null, ecran.colonnes ?? []);

  const gestesParPorte = new Map<string, GestesDeFiche<FicheGenerique>>(
    ecran.portes.map((porte) => {
      const depot = depotDePorte(porte.id);
      if (depot === null) return [porte.id, {}];
      /* LE FICHIER DIT LESQUELS des trois gestes l'ecran offre, ce code dit
         COMMENT. Le moteur fait le reste : la confirmation, la fermeture de la
         fiche, le rechargement de la porte et le compte rendu par `saveState`.
         `remove` est la suppression du socle (`archivedAt` + `deletedAt`),
         jamais un delete dur : le SDK ne l'expose meme pas. */
      return [
        porte.id,
        verbesDe<FicheGenerique>(ecran, {
          archive: async (record) => {
            await depot.archive(String(record['id'] ?? ''));
          },
          unarchive: async (record) => {
            await depot.unarchive(String(record['id'] ?? ''));
          },
          remove: async (record) => {
            await depot.remove(String(record['id'] ?? ''));
          },
        }),
      ];
    }),
  );

  return {
    ecran,
    portes,
    porteArrivee,
    vueArrivee,
    types,
    tri,
    champsDePorte,
    jeuDePorte,
    depotDePorte,
    bindings: (porteId) => bindingsParPorte.get(porteId) ?? [],
    filtres: (porteId, contexte = {}) => filtresDe(ecran, porteId, champsDePorte(porteId), lecture, fonctions, { timezone: sdk.timezone, ...contexte }),
    favoris: (porteId) => favorisDe(ecran, porteId),
    tranche: (porteId, contexte = {}) => trancheDe(ecran, porteId, lecture, { timezone: sdk.timezone, ...contexte }),
    actions: (porteId) => actionsDe(ecran, porteId, effetsDeLEcran, lireContexte),
    /* LES CHAMPS DU JEU disent aux stats qu'un champ declare est une personne :
       ni plus petit ni plus grand d'un identifiant (`statsDe`). */
    stats: (porteId) => statsDe(ecran, porteId, lecture, fonctions, champsDePorte(porteId)),
    gestes: (porteId) => gestesParPorte.get(porteId) ?? {},
    jeux: avecDepot,
    clefDeJeuDePorte,
  };
}
